import { memo } from "react";
import { harshness } from "utils/calibrationCampaign";

const COLORS = { mild: "#059669", moderate: "#d97706", harsh: "#dc2626" };

/**
 * The campaign's conditions as numbered points on temperature x humidity, inside the chamber's reachable range
 * (the shaded box), so it is easy to see whether the plan covers the harsh and the mild corners.
 */
function ConditionMap({ conditions, chamber }) {
  const W = 260;
  const H = 180;
  const pad = 28;
  const tMin = 15;
  const tMax = 65;
  const rhMin = 20;
  const rhMax = 100;
  const x = (t) => pad + ((t - tMin) / (tMax - tMin)) * (W - pad - 8);
  const y = (rh) => H - pad - ((rh - rhMin) / (rhMax - rhMin)) * (H - pad - 8);
  const points = conditions
    .map((c, i) => ({ i, t: Number(c.temp_c), rh: Number(c.rh_pct) }))
    .filter((p) => Number.isFinite(p.t) && Number.isFinite(p.rh) && p.t >= tMin && p.t <= tMax && p.rh >= rhMin && p.rh <= rhMax);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full max-w-xs" role="img" aria-label="Conditions on temperature and humidity">
      {chamber && (
        <rect
          x={x(chamber.temp_min_c)}
          y={y(chamber.rh_max_pct)}
          width={x(chamber.temp_max_c) - x(chamber.temp_min_c)}
          height={y(chamber.rh_min_pct) - y(chamber.rh_max_pct)}
          fill="#eff6ff"
          stroke="#bfdbfe"
        />
      )}
      <line x1={pad} y1={H - pad} x2={W - 8} y2={H - pad} stroke="#94a3b8" />
      <line x1={pad} y1={8} x2={pad} y2={H - pad} stroke="#94a3b8" />
      {[20, 30, 40, 50, 60].map((t) => (
        <text key={t} x={x(t)} y={H - pad + 12} fontSize="9" textAnchor="middle" fill="#64748b">{t}°</text>
      ))}
      {[40, 60, 80, 100].map((rh) => (
        <text key={rh} x={pad - 4} y={y(rh) + 3} fontSize="9" textAnchor="end" fill="#64748b">{rh}%</text>
      ))}
      <text x={W - 8} y={H - 4} fontSize="9" textAnchor="end" fill="#64748b">temperature</text>
      {points.map((p) => (
        <g key={p.i}>
          <circle cx={x(p.t)} cy={y(p.rh)} r="8" fill={COLORS[harshness({ temp_c: p.t, rh_pct: p.rh })] || "#64748b"} opacity="0.9" />
          <text x={x(p.t)} y={y(p.rh) + 3} fontSize="9" fontWeight="bold" textAnchor="middle" fill="white">{p.i + 1}</text>
        </g>
      ))}
    </svg>
  );
}

export default memo(ConditionMap);
